import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarCheck2,
  Check,
  Loader2,
  Minus,
  PencilLine,
  Plus,
  RefreshCw,
  ShieldAlert,
  Siren,
  Stethoscope,
} from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { StatusBadge } from '../../ui/StatusBadge/StatusBadge'
import { useScopedToast } from '../../ui/Toaster/useScopedToast'
import type { ServiceCatalogResponse } from './assistant-settings-contracts'
import type { AssistantSettingsRepository } from './assistant-settings-repository'
import {
  serviceBookingUpdateSchema,
  type BookingService,
  type ServiceBookingSettings,
} from './provider-resource-contracts'
import type { ProviderResourceRepository } from './provider-resource-repository'
import {
  ServiceEditDialog,
  type ServiceEditActions,
  type ServiceEditErrors,
  type ServiceRemoval,
} from './ServiceEditDialog'
import {
  removeClinicService,
  ServiceRemovalError,
  type ServiceRemovalTarget,
} from './remove-clinic-service'
import { SettingsConfirmDialog } from './SettingsConfirmDialog'
import { useReportUnsavedChanges } from './settings-unsaved-changes'
import panel from './SettingsSection.module.css'
import styles from './ServicesSettings.module.css'

export type InformationalService = ServiceCatalogResponse['services'][number]
export type BookingServiceDraft = Omit<BookingService, 'patientAgePolicy'> & {
  // UI-only deactivation: retain the exact mapping until Save or undo.
  disabled?: true
  patientAgePolicy?: { minimumYears: number | ''; maximumYears: number | '' }
}

function agePolicyError(policy: BookingServiceDraft['patientAgePolicy']) {
  if (!policy) return null
  if (
    [policy.minimumYears, policy.maximumYears].some(
      (bound) =>
        typeof bound !== 'number' ||
        !Number.isInteger(bound) ||
        bound < 0 ||
        bound > 130
    )
  )
    return 'Enter both age bounds as whole numbers from 0 to 130.'
  if (policy.minimumYears > policy.maximumYears)
    return 'Minimum age must not exceed maximum age.'
  return null
}

// The canonical designation rule (mapping owner): at most one Emergency
// service, as one ungrouped service or every variant of one intent group.
export function emergencyDesignationError(services: BookingServiceDraft[]) {
  const marked = services.filter((service) => service.emergencyService)
  if (marked.length === 0) return null
  const groups = new Set(marked.map((service) => service.patientFacingIntent))
  if (groups.size > 1 || (groups.has(undefined) && marked.length > 1))
    return 'Mark only one service as the Emergency service.'
  const [group] = [...groups]
  if (
    group &&
    services.some(
      (service) =>
        service.patientFacingIntent === group && !service.emergencyService
    )
  )
    return 'Mark every variant of the Emergency service group.'
  return null
}

// Canonical fields/order, not object insertion order or response array order.
// Blank bounds stay invalid drafts; they must never be coerced into age zero.
function bookingServicesKey(services: BookingServiceDraft[]) {
  return JSON.stringify(
    services
      .map((service) => ({
        serviceIntent: service.serviceIntent,
        patientFacingIntent: service.patientFacingIntent,
        serviceLabel: service.serviceLabel,
        informationalServiceId: service.informationalServiceId,
        appointmentTypeId: service.appointmentTypeId,
        patientTypePolicy: service.patientTypePolicy,
        patientAgePolicy: service.patientAgePolicy
          ? {
              minimumYears: service.patientAgePolicy.minimumYears,
              maximumYears: service.patientAgePolicy.maximumYears,
            }
          : undefined,
        emergencyService: service.emergencyService ? true : undefined,
      }))
      .sort((left, right) =>
        left.serviceIntent.localeCompare(right.serviceIntent)
      )
  )
}

export type UnifiedServiceRow = {
  rowId: string
  informational?: InformationalService
  booking?: BookingServiceDraft
  linkageMissing: boolean
}

function newServiceId() {
  return `service-${crypto.randomUUID().replaceAll('-', '').slice(0, 24)}`
}

function serviceIntent(label: string, existing: ReadonlySet<string>) {
  const base =
    label
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 58) || 'CLINIC_SERVICE'
  let candidate = base
  let suffix = 2
  while (existing.has(candidate)) {
    candidate = `${base.slice(0, 61)}_${suffix}`
    suffix += 1
  }
  return candidate
}

export function composeServiceRows(
  informational: InformationalService[],
  booking: BookingServiceDraft[]
): UnifiedServiceRow[] {
  const informationalById = new Map(
    informational.map((service) => [service.serviceId, service])
  )
  const linkedIds = new Set<string>()
  const rows: UnifiedServiceRow[] = booking.map((service) => {
    const linked = service.informationalServiceId
      ? informationalById.get(service.informationalServiceId)
      : undefined
    if (linked) linkedIds.add(linked.serviceId)
    return {
      rowId: `booking:${service.serviceIntent}`,
      informational: linked,
      booking: service,
      linkageMissing: !linked,
    }
  })
  for (const service of informational) {
    if (!linkedIds.has(service.serviceId)) {
      rows.push({
        rowId: `service:${service.serviceId}`,
        informational: service,
        linkageMissing: false,
      })
    }
  }
  return rows.sort((left, right) => {
    const leftLabel =
      left.informational?.name ?? left.booking?.serviceLabel ?? left.rowId
    const rightLabel =
      right.informational?.name ?? right.booking?.serviceLabel ?? right.rowId
    return leftLabel.localeCompare(rightLabel)
  })
}

// One service, keyed by the same canonical fields as bookingServicesKey.
function bookingServiceKey(service: BookingServiceDraft) {
  return bookingServicesKey([service])
}

type RowChange = 'new' | 'changed' | null

// Whether a row differs from the saved settings, for its "Unsaved" marker.
function rowChange(
  row: UnifiedServiceRow,
  savedCatalog: InformationalService[],
  savedBooking: BookingServiceDraft[]
): RowChange {
  const savedInfo = row.informational
    ? savedCatalog.find(
        (service) => service.serviceId === row.informational!.serviceId
      )
    : undefined
  const saved = row.booking
    ? savedBooking.find(
        (service) => service.serviceIntent === row.booking!.serviceIntent
      )
    : undefined
  if (row.informational && !savedInfo && !saved) return 'new'
  if (
    row.informational &&
    (!savedInfo ||
      savedInfo.name !== row.informational.name ||
      savedInfo.availability !== row.informational.availability)
  )
    return 'changed'
  if (row.booking) {
    const active = !row.booking.disabled
    if (active !== Boolean(saved)) return 'changed'
    if (
      active &&
      saved &&
      bookingServiceKey(row.booking) !== bookingServiceKey(saved)
    )
      return 'changed'
  }
  return null
}

const INTENT_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/

type AppointmentTypes = NonNullable<
  ServiceBookingSettings['configuration']
>['appointmentTypes']

// What staff should look at before booking works for a row.
function rowProblems(
  row: UnifiedServiceRow,
  appointmentTypes: AppointmentTypes
) {
  const problems: string[] = []
  const label = row.informational?.name ?? row.booking?.serviceLabel ?? ''
  if (!label.trim()) problems.push('Name missing')
  const booking = row.booking && !row.booking.disabled ? row.booking : undefined
  if (!booking) return problems
  if (row.linkageMissing) problems.push('Not linked to a catalog service')
  const type = appointmentTypes.find(
    (item) => item.id === booking.appointmentTypeId
  )
  if (!type) problems.push('Appointment type no longer available')
  else if (!type.eligible)
    problems.push('Appointment type not ready for booking')
  if (agePolicyError(booking.patientAgePolicy))
    problems.push('Age range needs fixing')
  if (
    booking.patientFacingIntent &&
    !INTENT_PATTERN.test(booking.patientFacingIntent)
  )
    problems.push('Variant group needs fixing')
  return problems
}

const PATIENT_TYPES = {
  BOTH: 'New and existing',
  NEW: 'New patients',
  EXISTING: 'Existing patients',
} as const

type EditTarget = {
  // Booking variants keep their internal key; catalog-only rows their ID. A
  // row keeps one of the two while it is edited, even when it gains the other.
  serviceId?: string
  serviceIntent?: string
  isNew: boolean
  // Cancel restores this; edits are measured against `baseline` (the same
  // drafts, plus the blank row when the service is new).
  snapshot: {
    informational: InformationalService[]
    booking: BookingServiceDraft[]
  }
  baseline: string
}

function draftKey(
  informational: InformationalService[],
  booking: BookingServiceDraft[]
) {
  return JSON.stringify({ informational, booking })
}

function findRow(rows: UnifiedServiceRow[], target: EditTarget) {
  return rows.find(
    (row) =>
      (target.serviceIntent &&
        row.booking?.serviceIntent === target.serviceIntent) ||
      (target.serviceId && row.informational?.serviceId === target.serviceId)
  )
}

// Flio can't run without one bookable variant (once booking is set up) or one
// service on for AI answers, so the last of either stays.
const LAST_BOOKABLE =
  'It’s the only service Flio books, so it can’t be removed. Make another service bookable first.'
const KEEP_BOOKING =
  'Flio booking stays on for at least one service. Turn it on for another service first.'
const KEEP_AI =
  'AI answers stay on for at least one service, so Flio’s phone assistant has a service to describe. Turn them on for another service first.'
const LAST_AI =
  'It’s the only service with AI answers on, so it can’t be removed. Turn on AI answers for another service first.'

// What removing a saved service changes, for the confirmation.
function removalConsequences(
  row: UnifiedServiceRow,
  savedBooking: BookingServiceDraft[]
) {
  const lines: string[] = []
  const answers = row.informational?.availability === true
  const booking = row.booking && !row.booking.disabled ? row.booking : undefined
  if (answers)
    lines.push('Flio stops answering questions about it on calls and texts.')
  if (booking)
    lines.push(
      'Flio stops booking it. Appointments already booked are not changed.'
    )
  if (!answers && !booking)
    lines.push(
      'It is already off for AI answers and Flio booking; removing it takes it off this list.'
    )
  if (
    booking?.emergencyService &&
    !savedBooking.some(
      (service) =>
        service.serviceIntent !== booking.serviceIntent &&
        service.emergencyService
    )
  )
    lines.push(
      'It is your Emergency service. No service is marked for emergency visits until you mark another one.'
    )
  const group = booking?.patientFacingIntent
  if (
    group &&
    savedBooking.some(
      (service) =>
        service.serviceIntent !== booking.serviceIntent &&
        service.patientFacingIntent === group
    )
  )
    lines.push(`The other services in group ${group} stay as they are.`)
  lines.push(
    'Past calls, messages and appointments keep their history. Nothing changes in NexHealth.'
  )
  return lines
}

function YesNo({ on, label }: { on: boolean; label: string }) {
  return (
    <span className={styles.yesNo} data-on={on || undefined}>
      {on ? (
        <Check size={15} aria-hidden="true" />
      ) : (
        <Minus size={15} aria-hidden="true" />
      )}
      {on ? 'On' : 'Off'}
      <span className={panel.srOnly}> {label}</span>
    </span>
  )
}

export function ServicesSettings({
  tenantId,
  tenantLabel,
  assistantRepository,
  providerResourceRepository,
}: {
  tenantId: string
  tenantLabel: string
  assistantRepository: AssistantSettingsRepository
  providerResourceRepository?: ProviderResourceRepository
}) {
  const queryClient = useQueryClient()
  const notify = useScopedToast()
  const bookingEnabled = Boolean(providerResourceRepository?.getServiceBooking)
  const catalogQuery = useQuery({
    queryKey: ['settings', tenantId, 'service-catalog'],
    queryFn: () => assistantRepository.getServiceCatalog(tenantId),
  })
  const bookingQuery = useQuery({
    queryKey: ['settings', tenantId, 'service-booking'],
    queryFn: () => providerResourceRepository!.getServiceBooking!(tenantId),
    enabled: bookingEnabled,
  })
  const [informational, setInformational] = useState<InformationalService[]>([])
  const [bookingDrafts, setBooking] = useState<BookingServiceDraft[]>([])
  const booking = useMemo(
    () => bookingDrafts.filter((service) => !service.disabled),
    [bookingDrafts]
  )
  const [bookingBase, setBookingBase] =
    useState<ServiceBookingSettings['configuration']>(null)
  const [editing, setEditing] = useState<EditTarget | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const [removalError, setRemovalError] = useState<string>()
  const heading = useRef<HTMLHeadingElement>(null)
  // Whether the clinic's Voice assistant serves the saved catalog: server
  // state, so a reload or another session sees it too. Flio converges it
  // automatically; the retry only asks for it sooner.
  const voiceCatalogQuery = useQuery({
    queryKey: ['settings', tenantId, 'voice-service-catalog'],
    queryFn: () => assistantRepository.getVoiceServiceCatalogStatus(tenantId),
  })
  const voiceCatalog = voiceCatalogQuery.data
  const voiceSyncPending =
    voiceCatalog?.state === 'PENDING' || voiceCatalog?.state === 'BLOCKED'

  useEffect(() => {
    if (catalogQuery.data) setInformational(catalogQuery.data.services)
  }, [catalogQuery.data])
  useEffect(() => {
    if (
      bookingQuery.data?.configuration &&
      (!bookingBase ||
        bookingServicesKey(booking) ===
          bookingServicesKey(bookingBase.services))
    ) {
      setBookingBase(bookingQuery.data.configuration)
      setBooking(bookingQuery.data.configuration.services)
    }
  }, [bookingQuery.data?.configuration, booking, bookingBase])

  const configuration = bookingQuery.data?.configuration
  const appointmentTypes = configuration?.appointmentTypes ?? []
  const eligibleAppointmentTypes = appointmentTypes.filter(
    (item) => item.eligible
  )
  const rows = useMemo(
    () => composeServiceRows(informational, bookingDrafts),
    [bookingDrafts, informational]
  )
  const linkedIds = booking
    .map((service) => service.informationalServiceId)
    .filter((value): value is string => Boolean(value))
  const informationalValid =
    informational.length <= 200 &&
    informational.every(
      (service) =>
        /^[A-Za-z0-9_-]{1,128}$/.test(service.serviceId) &&
        service.name.trim().length > 0 &&
        service.name.trim().length <= 160
    ) &&
    new Set(informational.map((service) => service.serviceId)).size ===
      informational.length
  const bookingUpdate = serviceBookingUpdateSchema.safeParse({
    expectedMappingRevision: bookingBase?.mappingRevision ?? 0,
    expectedMappingFingerprint: bookingBase?.mappingFingerprint ?? 'MISSING',
    services: booking,
  })
  const groupKeys = new Set(
    booking.map((service) => service.patientFacingIntent).filter(Boolean)
  )
  const groupCollision = booking.some(
    (service) =>
      groupKeys.has(service.serviceIntent) &&
      service.patientFacingIntent !== service.serviceIntent
  )
  const emergencyError = emergencyDesignationError(booking)
  const bookingValid =
    !groupCollision &&
    !emergencyError &&
    bookingUpdate.success &&
    booking.length <= 100 &&
    booking.every((service) => {
      const appointmentType = appointmentTypes.find(
        (item) => item.id === service.appointmentTypeId
      )
      return (
        service.serviceLabel.trim().length > 0 &&
        service.serviceLabel.trim().length <= 120 &&
        /^[A-Z][A-Z0-9_]{0,63}$/.test(service.serviceIntent) &&
        appointmentType?.eligible === true &&
        (!service.patientAgePolicy ||
          service.patientAgePolicy.minimumYears <=
            service.patientAgePolicy.maximumYears)
      )
    }) &&
    new Set(booking.map((service) => service.serviceIntent)).size ===
      booking.length &&
    new Set(linkedIds).size === linkedIds.length
  const catalogChanged =
    Boolean(catalogQuery.data) &&
    JSON.stringify(informational) !==
      JSON.stringify(catalogQuery.data?.services ?? [])
  const bookingChanged =
    Boolean(bookingBase) &&
    bookingServicesKey(booking) !==
      bookingServicesKey(bookingBase?.services ?? [])
  const bookingConflict =
    bookingChanged &&
    Boolean(
      configuration &&
      bookingBase &&
      (configuration.mappingRevision !== bookingBase.mappingRevision ||
        configuration.mappingFingerprint !== bookingBase.mappingFingerprint)
    )
  const bookingEditable =
    bookingQuery.data?.status === 'AVAILABLE' ||
    bookingQuery.data?.status === 'REVIEW_REQUIRED'
  const dirty = catalogChanged || bookingChanged
  useReportUnsavedChanges('services', dirty)
  const save = useMutation({
    mutationFn: async () => {
      let savedCatalog = catalogQuery.data
      let savedBooking = bookingQuery.data
      if (catalogChanged && catalogQuery.data) {
        savedCatalog = await assistantRepository.updateServiceCatalog(
          tenantId,
          {
            expectedFingerprint: catalogQuery.data.catalogFingerprint,
            services: informational,
          }
        )
      }
      if (
        bookingChanged &&
        bookingBase &&
        providerResourceRepository?.updateServiceBooking
      ) {
        if (!bookingUpdate.success)
          throw new Error('Invalid service booking configuration')
        savedBooking = await providerResourceRepository.updateServiceBooking(
          tenantId,
          bookingUpdate.data
        )
      }
      let voiceSynced = true
      if (catalogChanged && savedCatalog) {
        try {
          await assistantRepository.reconcileVoiceServiceCatalog(
            tenantId,
            savedCatalog.catalogFingerprint
          )
        } catch {
          voiceSynced = false
        }
      }
      return { savedCatalog, savedBooking, voiceSynced }
    },
    onSuccess: ({ savedCatalog, savedBooking, voiceSynced }) => {
      if (savedCatalog) {
        queryClient.setQueryData(
          ['settings', tenantId, 'service-catalog'],
          savedCatalog
        )
        setInformational(savedCatalog.services)
      }
      if (savedBooking) {
        queryClient.setQueryData(
          ['settings', tenantId, 'service-booking'],
          savedBooking
        )
        if (savedBooking.configuration) {
          setBookingBase(savedBooking.configuration)
          setBooking(savedBooking.configuration.services)
        }
      }
      notify(
        'success',
        voiceSynced
          ? 'Services saved'
          : 'Services saved, but the AI assistant update is pending'
      )
      void queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'voice-service-catalog'],
      })
    },
    onError: () => {
      notify(
        'error',
        'Services could not be saved. Latest settings are being reloaded.'
      )
      void queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'service-catalog'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'service-booking'],
      })
    },
  })

  // Removal saves at once (it is its own confirmed action, not a draft edit).
  const remove = useMutation({
    mutationFn: async ({
      target,
    }: {
      target: ServiceRemovalTarget
      label: string
    }) => {
      const result = await removeClinicService(
        tenantId,
        target,
        assistantRepository,
        providerResourceRepository
      )
      let voiceSynced = true
      if (result.catalogRemoved) {
        try {
          await assistantRepository.reconcileVoiceServiceCatalog(
            tenantId,
            result.savedCatalog.catalogFingerprint
          )
        } catch {
          voiceSynced = false
        }
      }
      return { ...result, voiceSynced }
    },
    onSuccess: (result, { label }) => {
      queryClient.setQueryData(
        ['settings', tenantId, 'service-catalog'],
        result.savedCatalog
      )
      setInformational(result.savedCatalog.services)
      if (result.savedBooking) {
        queryClient.setQueryData(
          ['settings', tenantId, 'service-booking'],
          result.savedBooking
        )
        if (result.savedBooking.configuration) {
          setBookingBase(result.savedBooking.configuration)
          setBooking(result.savedBooking.configuration.services)
        }
      }
      setEditing(null)
      setRemovalError(undefined)
      save.reset()
      notify(
        'success',
        result.voiceSynced
          ? `${label} removed`
          : `${label} removed, but the AI assistant update is pending`
      )
      void queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'voice-service-catalog'],
      })
      // The row and its Edit button are gone; continue from the list.
      window.setTimeout(() => heading.current?.focus(), 0)
    },
    onError: (error) => {
      setRemovalError(
        error instanceof ServiceRemovalError &&
          error.reason === 'ASSOCIATION_CHANGED'
          ? 'This service changed since Services was loaded, so nothing was removed. The latest settings are loading; check them and try again.'
          : error instanceof ServiceRemovalError &&
              error.reason === 'BOOKING_AUTHORITY_UNAVAILABLE'
            ? 'Booking settings are unavailable right now, so this bookable service can’t be removed. Refresh Services and try again.'
            : error instanceof ServiceRemovalError &&
                error.reason === 'LAST_BOOKABLE_SERVICE'
              ? LAST_BOOKABLE
              : error instanceof ServiceRemovalError &&
                  error.reason === 'LAST_AI_SERVICE'
                ? LAST_AI
                : 'The service could not be removed. The latest settings are loading; check them and try again.'
      )
      void queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'service-catalog'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'service-booking'],
      })
    },
  })

  const retryVoiceSync = useMutation({
    mutationFn: async () => {
      if (!voiceCatalog) throw new Error('Voice catalog status unavailable')
      await assistantRepository.reconcileVoiceServiceCatalog(
        tenantId,
        voiceCatalog.catalogFingerprint
      )
    },
    onSuccess: () =>
      notify('success', 'AI assistant updated with the saved services'),
    onError: () =>
      notify(
        'error',
        'The AI assistant still needs the saved services. Flio keeps retrying automatically.'
      ),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'voice-service-catalog'],
      }),
  })

  function updateInformational(
    serviceId: string,
    update: (service: InformationalService) => InformationalService
  ) {
    setInformational((current) =>
      current.map((service) =>
        service.serviceId === serviceId ? update(service) : service
      )
    )
    save.reset()
  }

  // Marking a service designates it (with every variant of its intent group)
  // and unmarks every other service: there is one Emergency service.
  function setEmergencyService(target: BookingServiceDraft, checked: boolean) {
    const group = target.patientFacingIntent
    const member = (service: BookingServiceDraft) =>
      service.serviceIntent === target.serviceIntent ||
      (Boolean(group) && service.patientFacingIntent === group)
    setBooking((current) =>
      current.map((service) => {
        const { emergencyService: _previous, ...rest } = service
        void _previous
        return checked && member(service)
          ? { ...rest, emergencyService: true as const }
          : member(service) || checked
            ? rest
            : service
      })
    )
    save.reset()
  }

  function updateBooking(
    serviceIntentId: string,
    update: (service: BookingServiceDraft) => BookingServiceDraft
  ) {
    setBooking((current) =>
      current.map((service) =>
        service.serviceIntent === serviceIntentId ? update(service) : service
      )
    )
    save.reset()
  }

  // The same edits the inline controls used to make, for one row.
  function actionsFor(row: UnifiedServiceRow): ServiceEditActions {
    const label = row.informational?.name ?? row.booking?.serviceLabel ?? ''
    return {
      rename: (name) => {
        if (row.informational) {
          updateInformational(row.informational.serviceId, (service) => ({
            ...service,
            name,
          }))
        }
        if (row.booking) {
          updateBooking(row.booking.serviceIntent, (service) => ({
            ...service,
            serviceLabel: name,
          }))
        }
      },
      setAiAnswers: (checked) => {
        if (row.informational) {
          updateInformational(row.informational.serviceId, (service) => ({
            ...service,
            availability: checked,
          }))
          return
        }
        if (!checked || !row.booking) return
        const serviceId = newServiceId()
        setInformational((current) => [
          ...current,
          { serviceId, name: label, availability: true },
        ])
        updateBooking(row.booking.serviceIntent, (service) => ({
          ...service,
          informationalServiceId: serviceId,
        }))
      },
      setBooking: (checked) => {
        if (row.booking) {
          updateBooking(row.booking.serviceIntent, (service) => {
            if (!checked) return { ...service, disabled: true }
            const restored = { ...service }
            delete restored.disabled
            return restored
          })
          return
        }
        const appointmentType = eligibleAppointmentTypes[0]
        if (!checked || !appointmentType || !row.informational) return
        const linked = row.informational
        setBooking((current) => [
          ...current,
          {
            informationalServiceId: linked.serviceId,
            serviceIntent: serviceIntent(
              linked.name,
              new Set(current.map((item) => item.serviceIntent))
            ),
            serviceLabel: linked.name,
            appointmentTypeId: appointmentType.id,
            patientTypePolicy: appointmentType.patientTypePolicy ?? 'BOTH',
          },
        ])
        save.reset()
      },
      associate: (serviceId) => {
        if (!row.booking) return
        updateBooking(row.booking.serviceIntent, (service) => ({
          ...service,
          informationalServiceId: serviceId,
          serviceLabel:
            informational.find((item) => item.serviceId === serviceId)?.name ??
            service.serviceLabel,
        }))
      },
      setAppointmentType: (appointmentTypeId) => {
        if (!row.booking) return
        const selected = appointmentTypes.find(
          (item) => item.id === appointmentTypeId
        )
        updateBooking(row.booking.serviceIntent, (service) => ({
          ...service,
          appointmentTypeId,
          patientTypePolicy:
            selected?.patientTypePolicy ?? service.patientTypePolicy,
        }))
      },
      setPatientType: (patientTypePolicy) => {
        if (!row.booking) return
        updateBooking(row.booking.serviceIntent, (service) => ({
          ...service,
          patientTypePolicy,
        }))
      },
      setAgeRestriction: (checked) => {
        if (!row.booking) return
        updateBooking(row.booking.serviceIntent, (service) => ({
          ...service,
          patientAgePolicy: checked
            ? (bookingBase?.services.find(
                (saved) => saved.serviceIntent === service.serviceIntent
              )?.patientAgePolicy ?? { minimumYears: 0, maximumYears: 130 })
            : undefined,
        }))
      },
      setAgeBound: (bound, value) => {
        if (!row.booking) return
        updateBooking(row.booking.serviceIntent, (service) => ({
          ...service,
          patientAgePolicy: { ...service.patientAgePolicy!, [bound]: value },
        }))
      },
      setEmergency: (checked) => {
        if (row.booking) setEmergencyService(row.booking, checked)
      },
      setGroup: (value) => {
        if (!row.booking) return
        const group = value.trim().toUpperCase()
        updateBooking(row.booking.serviceIntent, (service) => {
          const updated = { ...service }
          if (group) updated.patientFacingIntent = group
          else delete updated.patientFacingIntent
          return updated
        })
      },
    }
  }

  function openEditor(row: UnifiedServiceRow) {
    setEditing({
      serviceId: row.informational?.serviceId,
      serviceIntent: row.booking?.serviceIntent,
      isNew: false,
      snapshot: { informational, booking: bookingDrafts },
      baseline: draftKey(informational, bookingDrafts),
    })
  }

  function addService() {
    const serviceId = newServiceId()
    const added = [
      ...informational,
      { serviceId, name: '', availability: true },
    ]
    setInformational(added)
    save.reset()
    setEditing({
      serviceId,
      isNew: true,
      snapshot: { informational, booking: bookingDrafts },
      baseline: draftKey(added, bookingDrafts),
    })
  }

  function cancelEditing() {
    if (!editing) return
    setInformational(editing.snapshot.informational)
    setBooking(editing.snapshot.booking)
    setEditing(null)
  }

  function discardAll() {
    setConfirmingDiscard(false)
    setInformational(catalogQuery.data?.services ?? [])
    if (bookingBase) setBooking(bookingBase.services)
    save.reset()
  }

  if (catalogQuery.isPending || (bookingEnabled && bookingQuery.isPending)) {
    return <Spinner label="Loading clinic services" />
  }
  if (catalogQuery.isError || (bookingEnabled && bookingQuery.isError)) {
    return (
      <StatePanel
        kind="error"
        title="Clinic services unavailable"
        description="The clinic service catalog or its booking authority could not be loaded safely."
      />
    )
  }

  const readiness = bookingQuery.data?.bookingReadiness?.decision
  const aiAvailableCount = informational.filter(
    (service) => service.availability
  ).length
  const bookableCount = booking.length
  const savedCatalog = catalogQuery.data?.services ?? []
  const savedBookingServices = bookingBase?.services ?? []
  const changes = rows.map((row) =>
    rowChange(row, savedCatalog, savedBookingServices)
  )
  const changedCount = changes.filter(Boolean).length
  // A clinic that has bookable services or AI-answer services must keep one:
  // an empty booking mapping can't be stored (and the server refuses it), and
  // Voice can't be built without a service to describe.
  const savedAiCount = savedCatalog.filter(
    (service) => service.availability
  ).length
  const bookingEmptied = savedBookingServices.length > 0 && booking.length === 0
  const aiEmptied =
    savedAiCount > 0 && !informational.some((service) => service.availability)
  const saveBlocked =
    bookingEmptied ||
    aiEmptied ||
    save.isPending ||
    bookingConflict ||
    !informationalValid ||
    !bookingValid ||
    !dirty ||
    (bookingChanged &&
      (!bookingEditable ||
        !configuration ||
        !providerResourceRepository?.updateServiceBooking))

  const editedRow = editing ? findRow(rows, editing) : undefined
  const editedLabel =
    editedRow?.informational?.name ?? editedRow?.booking?.serviceLabel ?? ''
  const editedBooking =
    editedRow?.booking && !editedRow.booking.disabled
      ? editedRow.booking
      : undefined
  const editErrors: ServiceEditErrors = editedRow
    ? {
        name: !editedLabel.trim()
          ? 'Enter a service name.'
          : editedBooking && editedLabel.trim().length > 120
            ? 'Use 120 characters or fewer for a service Flio books.'
            : undefined,
        age: agePolicyError(editedBooking?.patientAgePolicy) ?? undefined,
        group:
          editedBooking?.patientFacingIntent &&
          !INTENT_PATTERN.test(editedBooking.patientFacingIntent)
            ? 'Use a letter followed by letters, numbers or underscores, up to 64 characters.'
            : undefined,
      }
    : {}
  // Removal acts on the saved service, so it needs no other unsaved edits.
  const snapshotDirty = editing
    ? JSON.stringify(editing.snapshot.informational) !==
        JSON.stringify(savedCatalog) ||
      bookingServicesKey(
        editing.snapshot.booking.filter((service) => !service.disabled)
      ) !== bookingServicesKey(savedBookingServices)
    : false
  const savedRow = editing
    ? findRow(
        composeServiceRows(
          editing.snapshot.informational,
          editing.snapshot.booking
        ),
        editing
      )
    : undefined
  const removalLabel =
    savedRow?.informational?.name.trim() ||
    savedRow?.booking?.serviceLabel.trim() ||
    'Service'
  const removal: ServiceRemoval | undefined =
    editing && !editing.isNew && savedRow
      ? {
          label: removalLabel,
          pending: remove.isPending,
          error: removalError,
          blockedReason: save.isPending
            ? 'Wait for the save to finish.'
            : snapshotDirty
              ? 'Save or discard your other changes first. Removing a service saves right away.'
              : savedRow.booking && !bookingEditable
                ? 'Booking settings are unavailable right now. Refresh Services before removing a bookable service.'
                : savedRow.booking &&
                    editing.snapshot.booking.filter((item) => !item.disabled)
                      .length === 1
                  ? LAST_BOOKABLE
                  : savedRow.informational?.availability &&
                      !editing.snapshot.informational.some(
                        (item) =>
                          item.serviceId !==
                            savedRow.informational!.serviceId &&
                          item.availability
                      )
                    ? LAST_AI
                    : undefined,
          consequences: removalConsequences(savedRow, editing.snapshot.booking),
          onRemove: () => {
            // This dialog's own edits are dropped: removal acts on the saved
            // service as the page loaded it.
            setInformational(editing.snapshot.informational)
            setBooking(editing.snapshot.booking)
            setRemovalError(undefined)
            remove.mutate({
              target: {
                informationalServiceId: editing.serviceId,
                serviceIntent: editing.serviceIntent,
              },
              label: removalLabel,
            })
          },
        }
      : undefined
  const emergencyReplaces =
    editing && editedBooking?.emergencyService
      ? [
          ...new Set(
            editing.snapshot.booking
              .filter(
                (saved) =>
                  saved.emergencyService &&
                  !bookingDrafts.find(
                    (draft) => draft.serviceIntent === saved.serviceIntent
                  )?.emergencyService
              )
              .map((saved) => saved.serviceLabel)
          ),
        ]
      : []

  return (
    <section className={panel.section} aria-labelledby="services-title">
      <div
        className={`${panel.panel} ${styles.servicesPanel}`}
        {...((editing || confirmingDiscard ? { inert: '' } : {}) as {
          inert?: string
        })}
      >
        <header className={panel.panelHeader}>
          <div className={panel.heading}>
            <span className={panel.eyebrow}>Clinic services</span>
            <h2 id="services-title" ref={heading} tabIndex={-1}>
              Services
            </h2>
            <p>
              What Flio can answer questions about and book for{' '}
              <strong>{tenantLabel}</strong>.
            </p>
            <div className={styles.summary}>
              <span>
                {rows.length} {rows.length === 1 ? 'service' : 'services'} ·{' '}
                {aiAvailableCount} with AI answers · {bookableCount} bookable by
                Flio
              </span>
              {bookableCount > 0 ? (
                readiness === 'READY' || readiness === 'PROVIDER_GATED' ? (
                  <StatusBadge
                    tone="success"
                    icon={<CalendarCheck2 size={13} aria-hidden="true" />}
                  >
                    Flio booking ready
                    {readiness === 'PROVIDER_GATED' ? (
                      <span className={styles.badgeNote}>
                        {' '}
                        · confirmed live with NexHealth
                      </span>
                    ) : null}
                  </StatusBadge>
                ) : (
                  <StatusBadge
                    tone="warning"
                    icon={<AlertTriangle size={13} aria-hidden="true" />}
                  >
                    Flio booking not ready
                  </StatusBadge>
                )
              ) : null}
            </div>
          </div>
          <div className={panel.headerActions}>
            <Button
              type="button"
              variant="secondary"
              disabled={save.isPending || informational.length >= 200}
              onClick={addService}
            >
              <Plus size={16} aria-hidden="true" /> Add service
            </Button>
          </div>
        </header>

        {bookingQuery.data?.status === 'REVIEW_REQUIRED' ? (
          <div className={panel.notice} data-tone="warning" role="alert">
            <ShieldAlert aria-hidden="true" size={17} />
            <div>
              <strong>Booking review required</strong>
              <p>
                The clinic scheduling policy changed, but its reviewed service
                mapping could not be reconciled automatically. Review Special
                Rules and bookable services before booking resumes.
              </p>
            </div>
          </div>
        ) : null}
        {voiceSyncPending ? (
          <div className={panel.notice} data-tone="info" role="status">
            <RefreshCw aria-hidden="true" size={16} />
            <div>
              The AI assistant hasn’t finished updating to the saved services,
              and until it does AI calls may not be able to look up or book
              appointments. Flio is updating it automatically.
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={save.isPending || retryVoiceSync.isPending}
                  onClick={() => retryVoiceSync.mutate()}
                >
                  Retry AI assistant update
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {rows.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon} aria-hidden="true">
              <Stethoscope size={20} />
            </span>
            <h3>No clinic services configured yet</h3>
            <p>
              Add a service, then choose whether Flio answers questions about
              it, books it, or both.
            </p>
          </div>
        ) : (
          <div className={styles.tableFrame}>
            <table className={styles.table}>
              <caption className={panel.srOnly}>
                Clinic services for {tenantLabel}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col">AI answers</th>
                  <th scope="col">Flio booking</th>
                  <th scope="col">Appointment type</th>
                  <th scope="col">Patients</th>
                  <th scope="col">Ages</th>
                  <th scope="col">
                    <span className={panel.srOnly}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const label =
                    row.informational?.name ?? row.booking?.serviceLabel ?? ''
                  const active =
                    row.booking && !row.booking.disabled
                      ? row.booking
                      : undefined
                  const type = active
                    ? appointmentTypes.find(
                        (item) => item.id === active.appointmentTypeId
                      )
                    : undefined
                  const problems = rowProblems(row, appointmentTypes)
                  const change = changes[index]
                  return (
                    <tr
                      key={row.rowId}
                      data-changed={change ?? undefined}
                      data-attention={problems.length > 0 || undefined}
                    >
                      <th scope="row">
                        <span className={styles.serviceName}>
                          {label.trim() || 'Unnamed service'}
                        </span>
                        {active?.emergencyService ||
                        active?.patientFacingIntent ||
                        change ||
                        problems.length > 0 ? (
                          <span className={styles.chips}>
                            {active?.emergencyService ? (
                              <span
                                className={styles.chip}
                                data-tone="emergency"
                              >
                                <Siren size={12} aria-hidden="true" /> Emergency
                              </span>
                            ) : null}
                            {active?.patientFacingIntent ? (
                              <span className={styles.chip}>
                                Group {active.patientFacingIntent}
                              </span>
                            ) : null}
                            {change ? (
                              <span className={styles.chip} data-tone="unsaved">
                                {change === 'new' ? 'New · unsaved' : 'Unsaved'}
                              </span>
                            ) : null}
                            {problems.length > 0 ? (
                              <span
                                className={styles.chip}
                                data-tone="warning"
                                title={problems.join(' · ')}
                              >
                                <AlertTriangle size={12} aria-hidden="true" />{' '}
                                Needs review
                                <span className={panel.srOnly}>
                                  : {problems.join(', ')}
                                </span>
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                      </th>
                      <td data-label="AI answers">
                        <YesNo
                          on={row.informational?.availability ?? false}
                          label="AI answers"
                        />
                      </td>
                      <td data-label="Flio booking">
                        <YesNo on={Boolean(active)} label="Flio booking" />
                      </td>
                      <td
                        data-label="Appointment type"
                        data-empty={!active || undefined}
                      >
                        {active ? (
                          <span className={styles.stack}>
                            <span>
                              {type?.name ?? 'Appointment type unavailable'}
                            </span>
                            {type ? <small>{type.minutes} min</small> : null}
                          </span>
                        ) : (
                          <span className={styles.none}>—</span>
                        )}
                      </td>
                      <td
                        data-label="Patients"
                        data-empty={!active || undefined}
                      >
                        {active ? (
                          PATIENT_TYPES[active.patientTypePolicy]
                        ) : (
                          <span className={styles.none}>—</span>
                        )}
                      </td>
                      <td data-label="Ages" data-empty={!active || undefined}>
                        {active ? (
                          active.patientAgePolicy ? (
                            <span className={styles.ages}>
                              {active.patientAgePolicy.minimumYears}–
                              {active.patientAgePolicy.maximumYears} yrs
                            </span>
                          ) : (
                            'All ages'
                          )
                        ) : (
                          <span className={styles.none}>—</span>
                        )}
                      </td>
                      <td className={styles.actionCell}>
                        <Button
                          type="button"
                          variant="secondary"
                          className={styles.editButton}
                          disabled={save.isPending || remove.isPending}
                          aria-label={`Edit ${label.trim() || 'unnamed service'}`}
                          onClick={() => openEditor(row)}
                        >
                          <PencilLine size={15} aria-hidden="true" /> Edit
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <footer className={panel.panelFooter} data-dirty={dirty || undefined}>
          <div className={panel.footerStatus} aria-live="polite">
            {save.isPending ? (
              <span className={panel.statusLine}>
                <Loader2 size={15} className={panel.spin} aria-hidden="true" />{' '}
                Saving services…
              </span>
            ) : dirty ? (
              <span className={panel.statusLine} data-tone="dirty">
                Unsaved changes
                {changedCount > 0
                  ? ` to ${changedCount} ${changedCount === 1 ? 'service' : 'services'}`
                  : ''}
              </span>
            ) : save.isSuccess ? (
              <span className={panel.statusLine} data-tone="saved">
                <Check size={16} aria-hidden="true" /> All changes saved
              </span>
            ) : (
              <span className={panel.statusLine}>No unsaved changes</span>
            )}
            {bookingConflict ? (
              <span className={panel.footerError} role="alert">
                Services changed since you began editing. Reload the latest
                services before editing again.
                <Button
                  type="button"
                  variant="secondary"
                  disabled={save.isPending}
                  onClick={() => {
                    if (configuration) {
                      setBookingBase(configuration)
                      setBooking(configuration.services)
                      save.reset()
                    }
                  }}
                >
                  Reload services
                </Button>
              </span>
            ) : null}
            {bookingEmptied ? (
              <span className={panel.footerError} role="alert">
                {KEEP_BOOKING}
              </span>
            ) : null}
            {aiEmptied ? (
              <span className={panel.footerError} role="alert">
                {KEEP_AI}
              </span>
            ) : null}
            {groupCollision ? (
              <span className={panel.footerError} role="alert">
                An intent group must not use another independent service’s
                internal key.
              </span>
            ) : null}
            {emergencyError ? (
              <span className={panel.footerError} role="alert">
                {emergencyError}
              </span>
            ) : null}
            {!informationalValid ? (
              <span className={panel.footerError} role="alert">
                Each service needs a nonempty name of at most 160 characters.
              </span>
            ) : null}
            {!bookingValid &&
            !emergencyError &&
            !booking.some((service) =>
              agePolicyError(service.patientAgePolicy)
            ) ? (
              <span className={panel.footerError} role="alert">
                Review the service names, unique associations and eligible
                appointment types before saving.
              </span>
            ) : null}
            {booking.some((service) =>
              agePolicyError(service.patientAgePolicy)
            ) ? (
              <span className={panel.footerError} role="alert">
                Fix the age range marked Needs review before saving.
              </span>
            ) : null}
            {bookingChanged &&
            (!bookingEditable ||
              !providerResourceRepository?.updateServiceBooking) ? (
              <span className={panel.footerError} role="alert">
                Booking configuration cannot be saved until its current
                authority is available. Refresh the Services configuration.
              </span>
            ) : null}
            {bookingQuery.isError ? (
              <span className={panel.footerError} role="alert">
                Booking configuration is unavailable; informational services can
                still be reviewed but booking changes cannot be saved.
              </span>
            ) : null}
          </div>
          <div className={panel.footerActions}>
            <Button
              type="button"
              variant="secondary"
              disabled={!dirty || save.isPending}
              onClick={() => setConfirmingDiscard(true)}
            >
              Discard changes
            </Button>
            <Button
              type="button"
              disabled={saveBlocked}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save services'}
            </Button>
          </div>
        </footer>
      </div>

      {editing && editedRow ? (
        <ServiceEditDialog
          row={editedRow}
          isNew={editing.isNew}
          changed={draftKey(informational, bookingDrafts) !== editing.baseline}
          appointmentTypes={appointmentTypes}
          eligibleTypeCount={eligibleAppointmentTypes.length}
          associationOptions={informational.filter(
            (service) =>
              !bookingDrafts.some(
                (other) =>
                  other.serviceIntent !== editedRow.booking?.serviceIntent &&
                  other.informationalServiceId === service.serviceId
              )
          )}
          savedBooking={savedBookingServices.some(
            (service) =>
              service.serviceIntent === editedRow.booking?.serviceIntent
          )}
          emergencyReplaces={emergencyReplaces}
          errors={editErrors}
          actions={actionsFor(editedRow)}
          removal={removal}
          keepOn={{
            booking:
              savedBookingServices.length > 0 &&
              editedBooking &&
              booking.length === 1
                ? KEEP_BOOKING
                : undefined,
            aiAnswers:
              savedAiCount > 0 &&
              editedRow.informational?.availability &&
              informational.filter((service) => service.availability).length ===
                1
                ? KEEP_AI
                : undefined,
          }}
          onApply={() => setEditing(null)}
          onCancel={cancelEditing}
        />
      ) : null}
      {confirmingDiscard ? (
        <SettingsConfirmDialog
          title="Discard unsaved changes?"
          confirmLabel="Discard changes"
          cancelLabel="Keep editing"
          onConfirm={discardAll}
          onCancel={() => setConfirmingDiscard(false)}
        >
          <p>
            Every service goes back to its saved settings. Nothing has been sent
            to Flio or NexHealth.
          </p>
        </SettingsConfirmDialog>
      ) : null}
    </section>
  )
}
